
import Modal from "@/components/base/modal/Modal"
import Card from "@/components/base/card/Card"
import Dropzone from "@/components/base/input/LegacyDropzone"
import ProgressLinear from "@/components/base/ProgressLinear"
import Button from "@/components/base/Button"
import TextArea from "@/components/base/input/TextArea"
import fileService from "@/services/file.service"
import materialService from "@/services/material.service"
import rules from "@/mixins/rules"
import { mdiClose } from "@mdi/js"
import ValidationResponse from "@/components/base/api/ValidationResponse"

export default {
    name: "InfoMaterialUpload",
    components: {
        ValidationResponse,
        Button,
        ProgressLinear,
        Dropzone,
        Card,
        Modal,
        TextArea,
    },
    mixins: [rules],
    /** update: indicates that an existing document was updated or a new one was created */
    emits: ["update"],
    props: {
        /** if the component is opened to edit an existing infomaterial  */
        editMode: {
            type: Boolean,
            default: false,
        },
        /** the id of an existing infomaterial (not File) that will be edited */
        editId: {
            type: Number,
            default: 0,
        },
        /** maximum size of an uploaded document (default: 100MB) */
        maxSize: {
            type: Number,
            default: 1e8,
        },
        /** array of allowed file types */
        acceptedFileTypes: {
            type: Array,
            default: () => [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "image/jpeg",
                "image/gif",
                "image/png",
                "image/webp",
                "image/svg+xml",
                "application/vnd.ms-excel",
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            ],
        },
    },
    data: function () {
        return {
            step: 0,
            showModal: false,
            showError: false,
            uploadedFile: null,
            errorMessage: null,
            validationResponseData: null,
            fileUploadProgress: 0,
            isFileUploaded: false,
            isInputValid: false,
            isUpdatingInfoMaterial: false,
            title: "",
            description: "",
            colsDropzone: 12,
            mdiClose,
        }
    },
    computed: {
        channelId() {
            if (this.$auth.user.region) {
                return this.$auth.user.region.channel.id
            } else if (this.$auth.user.school) {
                return this.$auth.user.school.channel.id
            }
            return undefined
        },
        isPublishBtnDisabled() {
            return (
                !this.isFileUploaded ||
                !this.isInputValid ||
                this.isUpdatingInfoMaterial
            )
        },
        isEditBtnDisabled() {
            return !this.isInputValid || this.isUpdatingInfoMaterial
        },
        fileIcon() {
            const typeToCheck = this.uploadedFile?.mime_type
            const imageMimeTypes = [
                "image/jpeg",
                "image/gif",
                "image/png",
                "image/webp",
                "image/svg+xml",
            ]
            if (typeToCheck && imageMimeTypes.includes(typeToCheck))
                return require("@/assets/icons/v2/image-2.3.svg")
            else return require("@/assets/icons/v2/document.1.svg")
        },
    },
    methods: {
        resetInputs() {
            this.title = ""
            this.description = ""
            this.uploadedFile = null
            this.isFileUploaded = false
        },
        async openModal() {
            if (this.editMode) {
                const response = await materialService.getMaterial(this.editId)
                const { title, file, description } = response.data

                this.uploadedFile = file
                this.title = title
                this.description = description
                this.step = 2
                this.isFileUploaded = true
            } else {
                this.step = 1
                this.resetInputs()
            }
            this.showModal = true
        },
        onModalClose() {
            this.step = 0
            this.resetInputs()
        },
        async handleFiles(files) {
            this.uploadedFile = null
            const file = files[0]
            if (!this.validateFile(file)) return
            this.uploadedFile = { ...file, mime_type: file.type }
            const formData = new FormData()
            formData.append("mime_type", file.type)
            formData.append("filename", file.name)
            formData.append("file", file)
            formData.append("filesize", file.size)
            formData.append("pdf_conversion", 0)

            this.step = 2
            this.fileUploadProgress = 0

            this.uploadedFile = await fileService.uploadFile(formData, {
                onUploadProgress: this.onFileUploadProgress,
            })
            this.title = this.uploadedFile?.["original_filename"]
            await this.$nextTick()
            this.isFileUploaded = true
        },
        validateFile(file) {
            this.errorMessage = null
            if (file && file.type && file.size) {
                if (!this.acceptedFileTypes.includes(file.type)) {
                    this.errorMessage =
                        "Dieses Format des Dokuments wird derzeit nicht unterstützt"
                }
                if (file.size > this.maxSize) {
                    this.errorMessage = "Das Dokument ist zu groß"
                }
            } else {
                this.errorMessage =
                    "Sie haben versucht, ein ungültiges Dokument oder keine Datei hochzuladen"
            }
            if (this.errorMessage) {
                this.showError = true
                return false
            }
            return true
        },
        onFileUploadProgress({ loaded, total }) {
            if (loaded && total)
                this.fileUploadProgress = (loaded / total) * 100
        },
        onDeleteBtnClick() {
            this.step = 1
            this.resetInputs()
        },
        async onPublishBtnClick() {
            this.isUpdatingInfoMaterial = true
            try {
                await materialService.postMaterial(
                    this.uploadedFile.id,
                    this.channelId,
                    this.title,
                    this.description
                )
                this.closeModalAndUpdate()
            } catch (e) {
                this.validationResponseData = e.response.data
            }
            this.isUpdatingInfoMaterial = false
        },
        async onEditBtnClick() {
            this.isUpdatingInfoMaterial = true
            try {
                await materialService.putMaterial(
                    this.editId,
                    this.uploadedFile.id,
                    this.title,
                    this.description
                )
                this.closeModalAndUpdate()
            } catch (e) {
                this.validationResponseData = e.response.data
            }
            this.isUpdatingInfoMaterial = false
        },
        async onDeleteInfoMaterialBtnClick() {
            this.isUpdatingInfoMaterial = true
            try {
                await materialService.deleteMaterial(this.editId)
                this.closeModalAndUpdate()
            } catch (e) {
                this.validationResponseData = e.response.data
            }
            this.isUpdatingInfoMaterial = false
        },
        closeModalAndUpdate() {
            this.$emit("update")
            this.showModal = false
            this.onModalClose()
        },
    },
}
